import { useCallback, useEffect, useState } from "react";
import { useSettings } from "../context/SettingsContext";
import {
  getPlaceConfig,
  mapStyles,
  normalizeMapView,
} from "../components/dashboard/functions.js";

export default function useConfiguredGoogleMap({ map, isLoaded }) {
  const { settings } = useSettings();
  const [placeMarkers, setPlaceMarkers] = useState([]);

  const mapDetailsArray = settings.mapDetails || [];
  const mapType = normalizeMapView(settings.mapview);

  useEffect(() => {
    if (!isLoaded || !map || !window.google?.maps?.places || !mapDetailsArray.length) {
      setPlaceMarkers([]);
      return;
    }

    const searchTimeout = setTimeout(() => {
      const mapCenter = map.getCenter();
      if (!mapCenter) {
        return;
      }

      const placeTypes = mapDetailsArray.filter(
        (detail) => detail !== "STREET_NAMES",
      );

      setPlaceMarkers([]);
      if (!placeTypes.length) {
        return;
      }

      let centerLat;
      let centerLng;
      try {
        centerLat = mapCenter.lat();
        centerLng = mapCenter.lng();
        if (isNaN(centerLat) || isNaN(centerLng)) {
          return;
        }
      } catch (error) {
        return;
      }

      const placesService = new window.google.maps.places.PlacesService(map);

      Promise.all(
        placeTypes.map(
          (placeType) =>
            new Promise((resolve) => {
              const placeConfig = getPlaceConfig(placeType);
              if (!placeConfig?.type) {
                resolve([]);
                return;
              }

              placesService.nearbySearch(
                {
                  location: new window.google.maps.LatLng(centerLat, centerLng),
                  radius: 20000,
                  type: placeConfig.type,
                },
                (results, status) => {
                  if (status === "OK" && results) {
                    resolve(
                      results.map((place) => ({
                        id: place.place_id,
                        name: place.name,
                        position: {
                          lat: place.geometry.location.lat(),
                          lng: place.geometry.location.lng(),
                        },
                        type: placeType,
                      })),
                    );
                    return;
                  }

                  resolve([]);
                },
              );
            }),
        ),
      ).then((allMarkers) => {
        setPlaceMarkers(allMarkers.flat());
      });
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [isLoaded, map, mapDetailsArray]);

  const getMapOptions = useCallback(
    (extraOptions = {}) => {
      const showStreetNames = mapDetailsArray.includes("STREET_NAMES");
      const baseOptions = {
        mapTypeId: mapType,
        styles: [
          ...mapStyles,
          {
            featureType: "road",
            elementType: "labels.text",
            stylers: [{ visibility: showStreetNames ? "on" : "off" }],
          },
          {
            featureType: "water",
            elementType: "labels.text",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "poi",
            elementType: "labels.text",
            stylers: [{ visibility: "on" }],
          },
        ],
      };

      return {
        ...baseOptions,
        ...extraOptions,
        styles: extraOptions.styles ?? baseOptions.styles,
      };
    },
    [mapDetailsArray, mapType],
  );

  return {
    settings,
    mapDetailsArray,
    mapType,
    placeMarkers,
    getMapOptions,
  };
}
